/**
 * @file TableComponent.ts
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el componente de TableComponent
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2024-12-19
 * @modified 2025-10-01
 * @author DLTCode
 * @author Sergio Manjón
 * @author Ismael Benabdellah
 * @author Pedro Luis Gómez
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */
"use client";

import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, OnChangeFn, PaginationState, SortingState, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import { PropsWithChildren, ReactNode, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Messages, useTranslations } from "next-intl";
import PaginationComponent from "@/components/ui/tables/PaginationComponent";
import SelectedRowsPopupComponent from "./SelectedRowsPopupComponent";
import RowsPerPageSelectComponent from "./RowsPerPageSelectComponent";
import { TableComponentProps } from "@/types/ui";
import { DEFAULT_PAGE, DEFAULT_SIZE } from "@/constants/filters";
import { ArrowDown, ArrowUp } from "lucide-react";
import TableSkeleton from "../skeletons/TableSkeleton";
const getNodeText = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join(" ");
  }
  if (isValidElement<{
    children?: ReactNode;
  }>(node)) {
    return getNodeText(node.props.children ?? "");
  }
  return "";
};
const getHeaderLabel = (headerDef: string | ((context: any) => ReactNode) | ReactNode, headerContext: unknown): string => {
  if (typeof headerDef === "string") {
    return headerDef;
  }
  if (typeof headerDef === "function") {
    const renderedHeader = headerDef(headerContext);
    return getNodeText(renderedHeader).replace(/\s+/g, " ").trim();
  }
  return getNodeText(headerDef).replace(/\s+/g, " ").trim();
};
const getSortDescriptionKey = (nextSortOrder: string | undefined): keyof Messages["TableComponent"]["Sort"] => {
  switch (nextSortOrder) {
    case "asc":
      return "asc";
    case "desc":
      return "desc";
    case "false":
      return "false";
    default:
      return "notSortable";
  }
};
const TableComponent = <TData extends {
  id: string;
},>({
  data,
  columns,
  showCheckboxes = true,
  page,
  totalData,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  singleRowSelection,
  selectedRowActions,
  isRowDisabled,
  caption,
  loading = false
}: TableComponentProps<TData>) => {
  const isMountedRef = useRef(false);
  const [isClientMounted, setIsClientMounted] = useState(false);
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [selectedRows, setSelectedRows] = useState<TData[]>([]);
  const handleClear = useCallback(() => setSelectedRows([]), [setSelectedRows]);
  const t = useTranslations("TableComponent");
  const sortT = useTranslations("TableComponent.Sort");
  const resolvedSorting = sorting ?? internalSorting;
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(updaterOrValue => {
    if (onSortingChange) {
      onSortingChange(updaterOrValue as Parameters<NonNullable<typeof onSortingChange>>[0]);
      return;
    }
    setInternalSorting(currentSorting => typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue);
  }, [onSortingChange]);
  useEffect(() => {
    isMountedRef.current = true;
    setIsClientMounted(true);
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const toggleRowSelection = useCallback((row: {
    original: TData;
  }) => {
    setSelectedRows(prev => prev.some(selectedRow_0 => selectedRow_0.id === row.original.id) ? prev.filter(selectedRow => selectedRow.id !== row.original.id) : [...prev, {
      ...row.original
    }]);
  }, []);
  const handleCheckboxKeyToggle = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.currentTarget.click();
    }
  };
  const handleRowClick = (row_0: ReturnType<typeof table.getRowModel>["rows"][number], e: React.MouseEvent) => {
    if (!showCheckboxes) return;
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT") return;

    // Check if row is disabled
    if (isRowDisabled?.(row_0.original)) return;
    const isAlreadySelected = selectedRows.some(selectedRow_1 => selectedRow_1.id === row_0.original.id);
    if (singleRowSelection) {
      if (isAlreadySelected) {
        setSelectedRows([]);
      } else if (selectedRows.length === 0) {
        setSelectedRows([{
          ...row_0.original
        }]);
      }
    } else {
      toggleRowSelection(row_0);
    }
  };
  const columnsWithSelection: ColumnDef<TData>[] = useMemo(() => {
    return [{
      id: "select",
      header: ({
        table: table_0
      }) => {
        if (singleRowSelection) return null;
        const allRowsDisabled = table_0.getRowModel().rows.every(row_1 => isRowDisabled?.(row_1.original));
        if (allRowsDisabled) return null;
        return <input type="checkbox" id="select-all-rows" aria-label={t("selectAllRows")} onKeyDown={handleCheckboxKeyToggle} onChange={e_0 => {
          const checked = e_0.target.checked;
          if (checked) {
            setSelectedRows(table_0.getRowModel().rows.filter(row_2 => !isRowDisabled?.(row_2.original)).map(row_3 => ({
              ...row_3.original
            })));
          } else {
            setSelectedRows([]);
          }
        }} checked={table_0.getRowModel().rows.length > 0 && table_0.getRowModel().rows.filter(row_4 => !isRowDisabled?.(row_4.original)).every(row_5 => selectedRows.some(sr => sr.id === row_5.original.id))} />;
      },
      cell: ({
        row: row_6
      }) => {
        const rowId = (row_6.original as {
          id: string;
        }).id;
        const checkboxId = `select-row-${rowId}`;
        const isSelected = selectedRows.some(sr_0 => sr_0.id === row_6.original.id);
        const isDisabled = isRowDisabled?.(row_6.original) ?? false;
        const disableOthers = singleRowSelection && selectedRows.length === 1 && !isSelected;
        return <input type="checkbox" id={checkboxId} aria-label={t("selectRow") + ` ${row_6.index + 1}`} checked={isSelected} onKeyDown={handleCheckboxKeyToggle} onChange={() => toggleRowSelection(row_6)} disabled={disableOthers || isDisabled} />;
      }
    }, ...columns];
  }, [columns, selectedRows, toggleRowSelection, singleRowSelection, t, isRowDisabled]);
  const table = useReactTable<TData>({
    data: data as TData[],
    columns: showCheckboxes ? columnsWithSelection : columns,
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: totalData?.total,
    manualPagination: !!totalData?.total,
    onPaginationChange: onPaginationChange as OnChangeFn<PaginationState>,
    manualSorting: onSortingChange !== undefined,
    enableMultiSort: onSortingChange !== undefined,
    isMultiSortEvent: () => onSortingChange !== undefined,
    sortDescFirst: false,
    state: {
      pagination: {
        pageIndex: pagination?.pageIndex ?? DEFAULT_PAGE,
        pageSize: pagination?.pageSize ?? DEFAULT_SIZE
      },
      sorting: resolvedSorting
    }
  });
  if (!isClientMounted) {
    return <TableSkeleton />;
  }
  const headerGroups = table.getHeaderGroups();
  const colSpan = headerGroups.length > 0 ? headerGroups[0].headers.length : 1;
  return <>
      <section className="table__container">
        <table className="table" aria-busy={loading}>
          {caption && <caption className="table__caption sr-only">{caption}</caption>}
          <thead className="table__head">
            {headerGroups.map(headerGroup => <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => <th className="table__head__item" key={header.id} colSpan={header.colSpan} scope="col" aria-sort={header.column.getCanSort() ? header.column.getIsSorted() === "asc" ? "ascending" : header.column.getIsSorted() === "desc" ? "descending" : "none" : undefined}>
                    {header.isPlaceholder ? null : <>
                        {(() => {
                  const headerContent = flexRender(header.column.columnDef.header, header.getContext());
                  const headerVisibleText = getHeaderLabel(header.column.columnDef.header, header.getContext());
                  const nextSortOrder = header.column.getCanSort() ? String(header.column.getNextSortingOrder()) : undefined;
                  const sortDescription = sortT(getSortDescriptionKey(nextSortOrder));
                  const Wrapper = ({
                    children
                  }: PropsWithChildren) => {
                    if (index === 0 && showCheckboxes
                    // && !singleRowSelection
                    ) return <div className={clsx(index === 0 && showCheckboxes && !singleRowSelection && "table__head__item__content__checkbox-wrapper")}>
                                  {children}
                                </div>;
                    return <button type="button" className={clsx("table__head__item__content", header.column.getCanSort() ? "table__head__item__content--sortable" : "table__head__item__content--not-sortable")} onClick={header.column.getToggleSortingHandler()} aria-label={headerVisibleText ? `${headerVisibleText}. ${sortDescription}` : undefined}>
                                {children}
                                <div className="table__head__item__content__sort">
                                  {header.column.getIsSorted() ? <>
                                      {{
                            asc: <ArrowUp role="img" aria-label={t("sortAscending")} size={16} />,
                            desc: <ArrowDown role="img" aria-label={t("sortDescending")} size={16} />
                          }[header.column.getIsSorted() as string]}
                                      {table.getState().sorting.length > 1 && <span>
                                          {header.column.getSortIndex() + 1}
                                        </span>}
                                    </> : <div className="table__head__item__content__sort__placeholder" />}
                                </div>
                              </button>;
                  };
                  return <Wrapper>{headerContent}</Wrapper>;
                })()}
                      </>}
                  </th>)}
              </tr>)}
          </thead>
          <tbody className="table__body">
            {data.length === 0 ? <tr>
                <td colSpan={colSpan} className="table__body__empty">
                  {t("empty")}
                </td>
              </tr> : table.getRowModel().rows.map(row_7 => {
            const isSelected_0 = selectedRows.some(selectedRow_2 => selectedRow_2.id === row_7.original.id);
            return <tr key={row_7.id} className={isSelected_0 ? "table__body__row--selected" : ""} onClick={e_1 => handleRowClick(row_7, e_1)}>
                    {row_7.getVisibleCells().map((cell, index_0) => <td className="table__body__item" key={cell.id}>
                        {(cell.getValue() == null || typeof cell.getValue() === "string" && (cell.getValue() as string).trim() === "" || Array.isArray(cell.getValue()) && (cell.getValue() as unknown[]).length === 0) && index_0 !== 0 ? "-" : flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>)}
                  </tr>;
          })}
          </tbody>
        </table>
      </section>

      {data.length > 0 && page && <>
          {onPaginationChange && <section className="table__paginationComponent">
              <PaginationComponent table={table} pageIndex={pagination?.pageIndex ?? DEFAULT_PAGE} pageCount={table.getPageCount()} />
              <RowsPerPageSelectComponent table={table} page={page} pageSize={pagination?.pageSize ?? DEFAULT_SIZE} rowCount={totalData?.total ?? 0} />
            </section>}
          <SelectedRowsPopupComponent selectedRows={selectedRows} onClear={handleClear} page={page}>
            {selectedRowActions ? selectedRowActions(selectedRows, handleClear) : null}
          </SelectedRowsPopupComponent>
        </>}
    </>;
};
export default TableComponent;